import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EllipsisVertical, LoaderCircle, MessageSquare, Pencil, Trash2, UserRound } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { ConfirmDialog } from '@/_shared/components/ConfirmDialog';
import { SearchInput } from '@/_shared/components/SearchInput';
import { Button } from '@/_shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/_shared/components/ui/dropdown-menu';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useChallengeCreators } from '@/_shared/hooks/useChallengeCreators';
import { useFocusShortcut } from '@/_shared/hooks/useFocusShortcut';
import { useIsMine } from '@/_shared/hooks/useIsMine';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';

import { RenameChatDialog } from '../components/RenameChatDialog';
import { useChatRename } from '../hooks/useChatRename';
import { BUCKET_LABELS, BUCKET_ORDER, groupByTemporalBucket, type TemporalBucket } from '../lib/dates';

/** Shown for a session that was never titled — the row, the search index and the dialog all use it. */
const UNTITLED = 'New conversation';

function formatOlderDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function ChatList(): React.ReactElement {
  usePageTitle('Chats');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { wp } = useWorkspace();
  const orpc = useOrpc();
  const { showCreators, nameFor } = useChallengeCreators();
  // Rename and delete are available only on the current user's chats.
  const isMine = useIsMine();
  const searchRef = useRef<HTMLInputElement>(null);
  useFocusShortcut('/', searchRef);

  const { data, isLoading } = useQuery(orpc.chat.list.queryOptions({ input: {} }));

  const allSessions = useMemo(() => data?.sessions ?? [], [data?.sessions]);

  const filteredSessions = useMemo(() => {
    if (!search.trim()) return allSessions;
    const q = search.toLowerCase();
    return allSessions.filter((s) => (s.title ?? UNTITLED).toLowerCase().includes(q));
  }, [allSessions, search]);

  const grouped = useMemo(() => groupByTemporalBucket(filteredSessions), [filteredSessions]);

  // Held in state, not looked up in query data: a refetch that drops the row (deleted in another tab,
  // renamed out of the filter) must not tear the open dialog and the user's draft out of the tree.
  const [renaming, setRenaming] = useState<{ id: string; title: string | null } | null>(null);
  const renameMutation = useChatRename();

  // Delete is confirmed — the menu stages the session; the dialog fires the mutation.
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // `?? UNTITLED` only for a session we actually found: `title` is legitimately null for an untitled chat,
  // so `?? null` sent it down the "row vanished" branch and the dialog stopped naming it.
  const deletingSession = deletingId === null ? undefined : allSessions.find((s) => s.id === deletingId);
  const deletingTitle = deletingSession ? (deletingSession.title ?? UNTITLED) : null;

  const deleteMutation = useMutation(
    orpc.chat.delete.mutationOptions({
      onSuccess: (_result, variables) => {
        void queryClient.invalidateQueries({
          queryKey: orpc.chat.list.queryOptions({ input: {} }).queryKey,
        });
        setDeletingId(null);
        toast.success('Chat deleted');
        // Leave the deleted conversation if it's the one open behind this list.
        if (location.pathname === wp(`/chat/${variables.id}`)) {
          void navigate(wp());
        }
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Failed to delete chat');
      },
    }),
  );

  const handleDelete = useCallback((sessionId: string) => setDeletingId(sessionId), []);

  const isSearching = search.trim().length > 0;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-lg font-semibold text-foreground">Chats</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">Your conversation history.</p>
          </div>
          <Button asChild>
            <Link to={wp()}>
              <MessageSquare className="size-4" />
              <span>New chat</span>
            </Link>
          </Button>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <LoaderCircle size={24} className="animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && allSessions.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <MessageSquare size={48} className="mb-4 text-muted-foreground/30" />
            <h2 className="text-base font-medium text-foreground">No conversations yet</h2>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">Start a new chat to begin.</p>
          </div>
        )}

        {!isLoading && allSessions.length > 0 && (
          <div className="mx-auto max-w-3xl px-6 pt-4 pb-2">
            <SearchInput
              ref={searchRef}
              value={search}
              onChange={setSearch}
              placeholder="Search chats…"
              shortcutHint="/"
              size="lg"
            />
          </div>
        )}

        {!isLoading && allSessions.length > 0 && filteredSessions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <p className="text-sm text-muted-foreground">No chats matching &lsquo;{search}&rsquo;</p>
            <button onClick={() => setSearch('')} className="mt-2 text-sm text-brand hover:underline">
              Clear search
            </button>
          </div>
        )}

        {!isLoading && filteredSessions.length > 0 && (
          <div className="mx-auto max-w-3xl px-6 py-2">
            {isSearching
              ? // Flat list when searching
                filteredSessions.map((session) => (
                  <ChatRow
                    key={session.id}
                    session={session}
                    onDelete={handleDelete}
                    onRename={setRenaming}
                    showDate
                    creatorName={showCreators ? nameFor(session.userId) : null}
                    canManage={isMine(session.userId)}
                  />
                ))
              : // Temporal buckets when not searching
                BUCKET_ORDER.map((bucket) => {
                  const items = grouped[bucket];
                  if (items.length === 0) return null;
                  return (
                    <BucketSection
                      key={bucket}
                      bucket={bucket}
                      sessions={items}
                      onDelete={handleDelete}
                      onRename={setRenaming}
                      creatorNameFor={showCreators ? nameFor : null}
                      isMine={isMine}
                    />
                  );
                })}
          </div>
        )}
      </div>

      <RenameChatDialog
        key={renaming?.id}
        open={renaming !== null}
        onOpenChange={(next) => {
          if (!next) setRenaming(null);
        }}
        title={renaming?.title ?? null}
        saving={renameMutation.isPending}
        onSave={async (title) => {
          if (renaming) await renameMutation.mutateAsync({ id: renaming.id, title });
        }}
      />

      <ConfirmDialog
        open={deletingId !== null}
        onOpenChange={(next) => {
          if (!next) setDeletingId(null);
        }}
        title="Delete this chat?"
        description={
          <>
            This permanently deletes {deletingTitle ? `"${deletingTitle}"` : 'the conversation'} and its message
            history. This can&apos;t be undone.
          </>
        }
        loading={deleteMutation.isPending}
        onConfirm={() => deletingId && deleteMutation.mutate({ id: deletingId })}
      />
    </div>
  );
}

function BucketSection({
  bucket,
  sessions,
  onDelete,
  onRename,
  creatorNameFor,
  isMine,
}: {
  bucket: TemporalBucket;
  sessions: { id: string; title: string | null; createdAt: string; userId: string }[];
  onDelete: (id: string) => void;
  onRename: (session: { id: string; title: string | null }) => void;
  creatorNameFor: ((userId: string | null | undefined) => string | null) | null;
  isMine: (userId: string | null | undefined) => boolean;
}): React.ReactElement {
  return (
    <div className="mb-2">
      <div className="sticky top-0 z-10 bg-background px-3 py-2">
        <span className="text-xs font-medium tracking-wider text-muted-foreground/60 uppercase">
          {BUCKET_LABELS[bucket]}
        </span>
      </div>
      {sessions.map((session) => (
        <ChatRow
          key={session.id}
          session={session}
          onDelete={onDelete}
          onRename={onRename}
          showDate={bucket === 'older' || bucket === 'week'}
          creatorName={creatorNameFor ? creatorNameFor(session.userId) : null}
          canManage={isMine(session.userId)}
        />
      ))}
    </div>
  );
}

function ChatRow({
  session,
  onDelete,
  onRename,
  showDate,
  creatorName,
  canManage,
}: {
  session: { id: string; title: string | null; createdAt: string; userId: string };
  onDelete: (id: string) => void;
  onRename: (session: { id: string; title: string | null }) => void;
  showDate?: boolean;
  creatorName?: string | null;
  /** Only the chat owner can rename or delete it. */
  canManage: boolean;
}): React.ReactElement {
  const { wp } = useWorkspace();
  const title = session.title ?? UNTITLED;
  return (
    <div className="group flex items-center gap-2 rounded-md px-2 transition-colors hover:bg-muted/50">
      <Link to={wp(`/chat/${session.id}`)} aria-label={title} className="flex min-w-0 flex-1 items-center gap-3 py-2.5">
        <MessageSquare size={14} className="shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate text-sm text-foreground">{title}</span>
        {creatorName && (
          <span className="flex shrink-0 items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[0.625rem] text-muted-foreground">
            <UserRound size={10} />
            {creatorName}
          </span>
        )}
        <span className="flex min-w-[5.5rem] shrink-0 justify-end">
          {showDate && (
            <span className="text-xs whitespace-nowrap text-muted-foreground">
              {formatOlderDate(session.createdAt)}
            </span>
          )}
        </span>
      </Link>
      <span className="flex w-6 shrink-0 justify-center">
        {canManage && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="tertiary"
                size="sm"
                aria-label={`Chat actions for ${title}`}
                Icon={<EllipsisVertical />}
                className="text-muted-foreground md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100 md:data-[state=open]:opacity-100"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => onRename({ id: session.id, title: session.title })}>
                <Pencil className="mr-2 size-3.5" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem variant="destructive" onSelect={() => onDelete(session.id)}>
                <Trash2 className="mr-2 size-3.5" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </span>
    </div>
  );
}
